/**
 * Cloud Function callable wrappers for the web-execute app.
 */
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '@/config/firebase';
import { ensureSessionReady } from '@/services/auth';
import type {
  ResolveTaskResult,
  UpdateStatusRequest,
  UpdateStatusResponse,
  UpdateDueDateRequest,
  RequestProofUploadRequest,
  RequestProofUploadResponse,
  UploadProofPhotoRequest,
  UploadProofPhotoResponse,
  EnsureContactForPhoneAuthResponse,
  FinalizeWebAssigneeResponse,
} from '../types';

async function call<TReq, TRes>(name: string, data: TReq): Promise<TRes> {
  await ensureSessionReady();
  const fn = httpsCallable<TReq, TRes>(getFirebaseFunctions(), name);
  const result = await fn(data);
  return result.data;
}

export async function resolveTaskForWeb(shortId: string): Promise<ResolveTaskResult> {
  return call('resolveTaskForWeb', { shortId });
}

export type ExecuteLinkResult =
  | { ok: true; shortId: string; url: string; purpose: string }
  | { ok: false; reason: string };

/**
 * The execute link for a task, minted if the owner never made one.
 *
 * The home lists tasks by id, but every execute callable is keyed by shortId.
 * Rather than duplicate that surface, an assignee trades their task id for the
 * link they were always the audience for, and the normal execute flow runs.
 */
export async function getExecuteLinkForTask(taskId: string): Promise<ExecuteLinkResult> {
  return call('generateTaskLink', { taskId, purpose: 'execute' });
}

export async function updateTaskStatusFromWeb(
  req: UpdateStatusRequest
): Promise<UpdateStatusResponse> {
  return call('updateTaskStatusFromWeb', req);
}

export async function updateTaskDueDateFromWeb(
  req: UpdateDueDateRequest
): Promise<UpdateStatusResponse> {
  return call('updateTaskDueDateFromWeb', req);
}

export async function requestProofUploadUrl(
  req: RequestProofUploadRequest
): Promise<RequestProofUploadResponse> {
  return call('requestProofUploadUrl', req);
}

/** Admin-SDK upload — avoids browser→GCS CORS on signed URL PUTs. */
export async function uploadProofPhotoFromWeb(
  req: UploadProofPhotoRequest
): Promise<UploadProofPhotoResponse> {
  return call('uploadProofPhotoFromWeb', req);
}

export async function ensureContactForPhoneAuth(): Promise<EnsureContactForPhoneAuthResponse> {
  return call('ensureContactForPhoneAuth', {});
}

export async function finalizeWebAssigneeAfterVerify(
  shortId: string,
  priorAnonymousUid?: string
): Promise<FinalizeWebAssigneeResponse> {
  return call('finalizeWebAssigneeAfterVerify', { shortId, priorAnonymousUid });
}

export async function rebindWebAcceptorAfterPhoneAuth(
  shortId: string,
  priorAnonymousUid?: string
): Promise<{ ok: boolean; rebound?: boolean }> {
  return call('rebindWebAcceptorAfterPhoneAuth', { shortId, priorAnonymousUid });
}
